import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type {
  CallInvitePayload,
  CallScreenPayload,
  CallCameraPayload,
  CallRosterPayload,
  CallStatePayload,
  ChatTarget,
  NotificationEvent,
  NotificationPreferencesResponse,
  NotificationSeverity,
  PresenceEntry,
  PresenceStatus,
} from "@xop/dashboard-shared";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  handleChatMessage,
  handleChatMessageUpdate,
  handleChatReaction,
} from "../chat/handleChatMessage.js";
import { toast } from "../../components/ui/use-toast.js";
import {
  deliverDesktopNotification,
  isTauri,
  onDesktopStatusCommand,
} from "./desktopBridge.js";
import { isAppFocused } from "./appFocus.js";
import { startIdleWatcher, type IdleWatcher } from "./activity.js";
import { startSystemIdleTracking } from "./systemIdle.js";
import { playNotificationSound } from "./sound.js";
import { startRealtimeSocket, type RealtimeSocket } from "./realtimeSocket.js";

/**
 * Mounts the single always-open realtime WebSocket for the session and
 * exposes its presence/status to the tree. Replaces the old
 * `useNotificationStream` SSE hook: same on-event rendering (desktop popup /
 * in-app snackbar / bell refresh — see `handleNotificationEvent`) PLUS the
 * bidirectional bits a socket enables — who's **online** and the user's
 * **status** (active / away / dnd), which the profile dropdown and the
 * desktop tray drive through `setStatus`.
 *
 * Mounted once high in `AppShell` so any descendant (the profile menu, a
 * future presence indicator) can read it via `useRealtime()`.
 */
export interface RealtimeContextValue {
  /** The user's current status (echoed by the server). */
  status: PresenceStatus;
  /** Set + persist + push the status to the server. */
  setStatus: (status: PresenceStatus) => void;
  /** Everyone currently online (incl. self), with their status. */
  online: PresenceEntry[];
  /** Whether the socket is currently connected. */
  connected: boolean;
  /** conversationKey (`room:all` / `dm:<id>`) → the name of whoever is
   * currently typing there. Auto-expires a few seconds after the last signal. */
  typingByConversation: Record<string, string>;
  /** conversationKey → the highest message id the OTHER party has read
   * (DM read receipts; drives the sent → read tick flip). */
  readByConversation: Record<string, number>;
  /** Emit a "typing…" signal for a conversation (throttle at the call site). */
  sendTyping: (target: ChatTarget) => void;
  /**
   * Report whether the user is "busy" (currently on a call — which includes
   * watching a screen share, since screen-share only happens inside a call).
   * While busy, auto-away is suppressed: the idle countdown is paused so a
   * mouse-still user on a call is never flipped to "away", and joining a call
   * while auto-away snaps you back to "active". Fed by `CallProvider` (a nested
   * provider) from its active-call state — the same parent/child seam as
   * `registerCallHandlers`. Passing `false` when a call ends grants a fresh full
   * idle window.
   */
  reportBusy: (busy: boolean) => void;
  /**
   * Register call-frame handlers from a nested provider (e.g. CallProvider).
   * The callbacks are stored in refs and invoked by RealtimeProvider whenever
   * the socket delivers a `call_invite`, `call_state`, or `call_screen` frame.
   * Returns an unregister function; call it in the consumer's cleanup effect.
   */
  registerCallHandlers: (handlers: {
    onCallInvite: (p: CallInvitePayload) => void;
    onCallState: (p: CallStatePayload) => void;
    onCallScreen: (p: CallScreenPayload) => void;
    onCallCamera: (p: CallCameraPayload) => void;
    onCallRoster: (p: CallRosterPayload) => void;
  }) => () => void;
}

const DEFAULT_VALUE: RealtimeContextValue = {
  status: "active",
  setStatus: () => undefined,
  online: [],
  connected: false,
  typingByConversation: {},
  readByConversation: {},
  sendTyping: () => undefined,
  reportBusy: () => undefined,
  registerCallHandlers: (_handlers) => () => undefined,
};

/** How long a "typing…" label lingers after the last signal (the sender
 * re-emits every few seconds while actively typing, so it stays lit). */
const TYPING_TTL_MS = 6_000;

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: ReactNode }): JSX.Element {
  const qc = useQueryClient();
  const [status, setStatusState] = useState<PresenceStatus>("active");
  const [online, setOnline] = useState<PresenceEntry[]>([]);
  const [connected, setConnected] = useState(false);
  const [typingByConversation, setTyping] = useState<Record<string, string>>({});
  const [readByConversation, setRead] = useState<Record<string, number>>({});
  const socketRef = useRef<RealtimeSocket | null>(null);
  const typingTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // The socket's `hello` frame carries our own user id (server-authenticated),
  // so we tell our own chat echoes from teammates' messages WITHOUT coupling
  // this provider to the auth context. Mirror status into a ref too, so the
  // once-created onChat closure reads the CURRENT status.
  const selfIdRef = useRef(0);
  const statusRef = useRef<PresenceStatus>(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);
  // The live idle watcher — held so `reportBusy` can pause/resume it and the
  // desktop OS-idle feed can push samples into it.
  const idleWatcherRef = useRef<IdleWatcher | null>(null);

  // Stable refs for registered call handlers — set by CallProvider via
  // registerCallHandlers() and read by the socket's onCallInvite/onCallState/
  // onCallScreen closures. Using refs avoids re-creating the socket when
  // handlers change.
  const callInviteHandlerRef = useRef<((p: CallInvitePayload) => void) | null>(null);
  const callStateHandlerRef = useRef<((p: CallStatePayload) => void) | null>(null);
  const callScreenHandlerRef = useRef<((p: CallScreenPayload) => void) | null>(null);
  const callCameraHandlerRef = useRef<((p: CallCameraPayload) => void) | null>(null);
  const callRosterHandlerRef = useRef<((p: CallRosterPayload) => void) | null>(null);

  useEffect(() => {
    const timers = typingTimers.current;
    const socket = startRealtimeSocket({
      onEvent: (ev) => handleNotificationEvent(qc, ev),
      onChat: (message) => handleChatMessage(qc, message, selfIdRef.current, statusRef.current),
      onMessageUpdate: (message) => handleChatMessageUpdate(qc, message, selfIdRef.current),
      onSelfId: (id) => {
        selfIdRef.current = id;
      },
      onTyping: ({ from, sender_name, scope }) => {
        const key = scope === "room" ? "room:all" : `dm:${from}`;
        setTyping((prev) => ({ ...prev, [key]: sender_name }));
        if (timers[key]) clearTimeout(timers[key]);
        timers[key] = setTimeout(() => {
          setTyping((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
          delete timers[key];
        }, TYPING_TTL_MS);
      },
      onRead: ({ reader_id, last_read_id }) => {
        const key = `dm:${reader_id}`;
        setRead((prev) =>
          (prev[key] ?? 0) >= last_read_id ? prev : { ...prev, [key]: last_read_id },
        );
      },
      onReaction: (info) => handleChatReaction(qc, info, selfIdRef.current),
      onCallInvite: (p) => callInviteHandlerRef.current?.(p),
      onCallState: (p) => callStateHandlerRef.current?.(p),
      onCallScreen: (p) => callScreenHandlerRef.current?.(p),
      onCallCamera: (p) => callCameraHandlerRef.current?.(p),
      onCallRoster: (p) => callRosterHandlerRef.current?.(p),
      onStatus: (s) => setStatusState(s),
      onPresence: (list) => setOnline(list),
      onOpen: () => setConnected(true),
      onClose: () => setConnected(false),
    });
    socketRef.current = socket;
    setStatusState(socket.getStatus()); // reflect the persisted status immediately
    return () => {
      socket.close();
      socketRef.current = null;
      for (const id of Object.values(timers)) clearTimeout(id);
    };
  }, [qc]);

  // Desktop tray ▸ Status ▸ (Available / Away / Do Not Disturb) emits a Tauri
  // event; apply it the same way the profile dropdown does so both surfaces
  // drive the one server-side status. No-op in the browser.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    let cancelled = false;
    void onDesktopStatusCommand((s) => {
      // Picking a status IS proof the user is present, so clear any auto-away
      // in the same breath — otherwise choosing "Available" from the tray (a
      // native menu the webview never sees as an event) would be overruled by
      // the still-idle watcher on the very next aggregation.
      idleWatcherRef.current?.noteActivity();
      setStatusState(s);
      socketRef.current?.setStatus(s);
    }).then((un) => {
      if (cancelled) un();
      else unlisten = un;
    });
    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, []);

  // Auto-away: report THIS client's idleness and let the server decide. We no
  // longer compute the resulting status locally — the server merges idleness
  // across every device the user has connected (and their explicit pick) and
  // echoes back the effective status, which lands via `onStatus`. That is what
  // stops a desktop app idling in the tray from marking someone away while
  // they're plainly working in a browser tab.
  useEffect(() => {
    const watcher = startIdleWatcher({
      onIdle: () => socketRef.current?.setIdle(true),
      onActive: () => socketRef.current?.setIdle(false),
    });
    idleWatcherRef.current = watcher;
    return () => {
      watcher.stop();
      idleWatcherRef.current = null;
    };
  }, []);

  // Desktop only: feed the OS-wide idle clock into the same watcher, so being
  // AT the computer counts as being present even when every click for the last
  // hour landed in another application. No-op in a browser, which cannot see
  // input outside its own window. See systemIdle.ts.
  useEffect(() => {
    let stop: (() => void) | null = null;
    let cancelled = false;
    void startSystemIdleTracking((seconds) => {
      idleWatcherRef.current?.noteSystemIdle(seconds);
    }).then((fn) => {
      if (cancelled) fn();
      else stop = fn;
    });
    return () => {
      cancelled = true;
      stop?.();
    };
  }, []);

  // Busy (on a call) suppresses auto-away. Joining a call while auto-away snaps
  // you back to "active" (you're clearly present); the watcher itself pauses the
  // idle countdown while busy and grants a fresh window when it clears. Stable
  // (no deps) so CallProvider can call it from an effect without churn.
  const reportBusy = useRef((busy: boolean) => {
    if (busy) socketRef.current?.setIdle(false);
    idleWatcherRef.current?.setBusy(busy);
  }).current;

  // Test/e2e seam: Cypress cannot drive a native call, so expose `reportBusy` on
  // `window` (mirrors the `__xopAutoAwayMs` window seam in activity.ts) to assert
  // that busy keeps a mouse-still user "active" past the idle window.
  useEffect(() => {
    if (typeof window === "undefined") return;
    (window as Window & { __xopReportBusy?: (b: boolean) => void }).__xopReportBusy =
      reportBusy;
    return () => {
      delete (window as Window & { __xopReportBusy?: (b: boolean) => void }).__xopReportBusy;
    };
  }, [reportBusy]);

  const registerCallHandlers = useRef(
    (handlers: {
      onCallInvite: (p: CallInvitePayload) => void;
      onCallState: (p: CallStatePayload) => void;
      onCallScreen: (p: CallScreenPayload) => void;
      onCallCamera: (p: CallCameraPayload) => void;
      onCallRoster: (p: CallRosterPayload) => void;
    }) => {
      callInviteHandlerRef.current = handlers.onCallInvite;
      callStateHandlerRef.current = handlers.onCallState;
      callScreenHandlerRef.current = handlers.onCallScreen;
      callCameraHandlerRef.current = handlers.onCallCamera;
      callRosterHandlerRef.current = handlers.onCallRoster;
      return () => {
        callInviteHandlerRef.current = null;
        callStateHandlerRef.current = null;
        callScreenHandlerRef.current = null;
        callCameraHandlerRef.current = null;
        callRosterHandlerRef.current = null;
      };
    },
  ).current;

  const value = useMemo<RealtimeContextValue>(
    () => ({
      status,
      online,
      connected,
      typingByConversation,
      readByConversation,
      setStatus: (s) => {
        // Picking a status IS proof the user is present, so clear any auto-away
      // in the same breath — otherwise choosing "Available" from the tray (a
      // native menu the webview never sees as an event) would be overruled by
      // the still-idle watcher on the very next aggregation.
      idleWatcherRef.current?.noteActivity();
        setStatusState(s); // optimistic — the server echo confirms
        socketRef.current?.setStatus(s); // explicit choice → persisted as base
      },
      sendTyping: (target) =>
        socketRef.current?.sendTyping(target.kind === "room" ? target.room : target.user_id),
      reportBusy,
      registerCallHandlers,
    }),
    [
      status,
      online,
      connected,
      typingByConversation,
      readByConversation,
      reportBusy,
      registerCallHandlers,
    ],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/**
 * Read the realtime context. Returns a safe inert default when used outside
 * a provider (e.g. an isolated component render in a test) rather than
 * throwing — presence/status is an enhancement, never load-bearing.
 */
export function useRealtime(): RealtimeContextValue {
  return useContext(RealtimeContext) ?? DEFAULT_VALUE;
}

/**
 * Render a freshly-arrived notification WITHOUT opening the app:
 *   - **desktop app:** the custom bottom-right popup card
 *     (`showDesktopNotification`), gated by the per-category desktop pref +
 *     global mute (`desktopAllowed`);
 *   - **browser:** the ambient in-app snackbar, colour-coded by severity.
 * Always refreshes the bell.
 *
 * **Sound is focus-gated:** a notification is audible whenever the app is NOT
 * focused (an away user needs the nudge) OR the event itself asks for a chime
 * (a high-signal @mention), gated by the same mute + desktop-pref as the
 * banner/snackbar. While the app is focused, ordinary notifications stay silent
 * (you can see the snackbar / banner). On desktop the OS-native banner carries
 * the sound (`sound:"default"`); in the browser it's the WebAudio chime.
 */
export function handleNotificationEvent(qc: QueryClient, ev: NotificationEvent): void {
  qc.invalidateQueries({ queryKey: ["notifications", "feed"] });
  const allowed = desktopAllowed(qc, ev);
  const audible = allowed && (ev.sound === true || !isAppFocused());
  if (isTauri()) {
    // The OS-native banner plays its own sound when `sound` is set (see
    // desktopBridge.notifyNative). Request it whenever the notification should
    // be audible; otherwise pass the event through untouched.
    if (allowed) void deliverDesktopNotification(audible ? { ...ev, sound: true } : ev);
  } else {
    toast({
      title: ev.title,
      description: ev.body,
      severity: toastSeverity(ev.severity),
    });
    // Best-effort WebAudio chime (autoplay policy) — never throws.
    if (audible) playNotificationSound();
  }
}

/** Notification severity → MUI Alert/snackbar severity. */
function toastSeverity(severity: NotificationSeverity): "info" | "warning" | "error" {
  if (severity === "critical") return "error";
  if (severity === "warn") return "warning";
  return "info";
}

function desktopAllowed(qc: QueryClient, ev: NotificationEvent): boolean {
  const prefs = qc.getQueryData<NotificationPreferencesResponse>([
    "notifications",
    "prefs",
  ]);
  if (!prefs) return true; // prefs not loaded yet — don't suppress
  if (prefs.muted) return false;
  const cell = prefs.preferences.find(
    (p) => p.category === ev.category && p.channel === "desktop",
  );
  return cell ? cell.enabled : true;
}
